import type { GameRules, MatchOutcome, Prediction } from "@obean/game-contract";
import type { NetFrame } from "@obean/sim-core";
import { NetClient, createRng } from "@obean/sim-core";
import type { DriverFrame, MatchDriver } from "./local-driver.js";

const rounded = (v: number) => (Number.isFinite(v) ? Math.round(v) : null);

/**
 * An online match (P2-RT-05). The Room DO is authoritative: this browser draws what sim-core's
 * NetClient makes of the room's snapshot frames, predicting its own player, and sends its input
 * once per step as `match.in`. It starts from the match's first frame, so there's always something
 * to draw.
 */
export class OnlineDriver<S, I> implements MatchDriver<S> {
  readonly mode = "online";
  readonly #net: NetClient<S, I>;
  readonly #input: () => I;
  #last: DriverFrame<S>;
  #outcome: MatchOutcome | null = null;
  #reported = { underruns: 0, freezes: 0, corrections: 0 };

  constructor(o: {
    rules: GameRules<S, I>;
    /** The game client's prediction of its own player, if it has one. */
    prediction: Prediction<S> | undefined;
    slot: number;
    players: number;
    /** The match's first snapshot frame. */
    first: NetFrame;
    /** This player's input now (sampled once per step). */
    input: () => I;
    send: (seq: number, input: I) => void;
    now: () => number;
  }) {
    this.#net = new NetClient<S, I>({
      rules: o.rules,
      slot: o.slot,
      players: o.players,
      ...(o.prediction ? { prediction: o.prediction } : {}),
      now: o.now,
      send: o.send,
    });
    this.#input = o.input;
    const state = o.rules.decodeSnapshot(o.first.game, createRng(0));
    this.#last = { from: state, to: state, alpha: 0, local: null, outcome: null };
    this.#net.receive(o.first);
  }

  /** A snapshot frame from the room. */
  receive(frame: NetFrame): void {
    this.#net.receive(frame);
  }

  /** The match is paused (a player is away): no input until `resume()` (P2-RT-06). */
  pause(): void {
    this.#net.pause();
  }

  resume(): void {
    this.#net.resume();
  }

  /** The page is going to the background: the room shouldn't hold the last input meanwhile. */
  release(): void {
    this.#net.release();
  }

  /** The match is decided (`match.end`): the host shows the result. */
  end(outcome: MatchOutcome): void {
    this.#outcome = outcome;
  }

  frame(): DriverFrame<S> {
    const drawn = this.#net.frame(this.#input);
    if (drawn) this.#last = { ...drawn, outcome: null };
    return { ...this.#last, outcome: this.#outcome };
  }

  netNow(): Record<string, number | null> {
    const { timeline, prediction, inputRtt } = this.#net.stats;
    return {
      input_rtt_p50_ms: rounded(inputRtt.p50),
      input_rtt_p95_ms: rounded(inputRtt.p95),
      snapshot_gap_p95_ms: rounded(timeline.gapMs.p95),
      buffer_ms: rounded(timeline.bufferMs),
      freezes_total: timeline.freezes,
      corrections_total: prediction?.hardSnaps ?? 0,
    };
  }

  netStats(): Record<string, number | null> {
    const { timeline, prediction, inputRtt } = this.#net.stats;
    const corrections = prediction?.hardSnaps ?? 0;
    const props = {
      input_rtt_p50_ms: rounded(inputRtt.p50),
      input_rtt_p95_ms: rounded(inputRtt.p95),
      snapshot_gap_p95_ms: rounded(timeline.gapMs.p95),
      buffer_ms: rounded(timeline.bufferMs),
      underruns: timeline.underruns - this.#reported.underruns,
      freezes: timeline.freezes - this.#reported.freezes,
      prediction_error_p95: prediction ? rounded(prediction.error.p95) : null,
      corrections: corrections - this.#reported.corrections,
    };
    this.#reported = { underruns: timeline.underruns, freezes: timeline.freezes, corrections };
    return props;
  }
}
